import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AddIcon, DeleteIcon } from '@/components/icons';
import { categoriesApi, type Category } from '@/api/categories';
import { MoneyText } from '@/components/MoneyText';
import { formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { UNCATEGORIZED, type AllocateMode, type AllocateRow } from './allocateRows';
import { useOwnedEnvelopes, type OwnedVirtualAccount } from './useOwnedEnvelopes';

/**
 * The one allocation destinations UI (TMMAPP-0109, made shared by TMMAPP-0110): the single allocate
 * dialog and the bulk one both render `DestinationGroupList` over the same row model
 * (`allocateRows.ts`) and its one preview rule. Groups are the virtual accounts the caller may
 * allocate into — the owner's and, since 0110, the ones they manage (MM-D-120) — each with its own
 * Unassigned first, then its envelopes; a row has a category and an amount, a percent or the rest;
 * "+ Add a row" adds another row to a group (audit 06 § Overrides 4).
 */

const MODES: readonly AllocateMode[] = ['amount', 'percent', 'rest'];

export interface Destination {
  virtualAccountId: number;
  envelopeId: number | null;
  name: string;
  balanceMinor: number | null;
}

export interface DestinationGroup {
  virtualAccount: OwnedVirtualAccount;
  destinations: Destination[];
}

type Contra = 'REFUND' | 'GIVE_BACK' | null;

/**
 * The destination groups, in `/me` order, and each virtual account's categories (MM-D-124: they
 * belong to the virtual account; every one is offered whatever the money's direction, MM-D-128).
 */
export function useDestinationGroups() {
  const { t } = useTranslation();
  const owned = useOwnedEnvelopes();
  const { envelopes, virtualAccounts } = owned;
  const groups = useMemo<DestinationGroup[]>(
    () =>
      virtualAccounts.map((virtualAccount) => ({
        virtualAccount,
        destinations: [
          {
            virtualAccountId: virtualAccount.id,
            envelopeId: null,
            name: t('virtualAccount.unassigned'),
            balanceMinor: virtualAccount.unassignedMinor,
          },
          ...envelopes
            .filter((envelope) => envelope.virtualAccountId === virtualAccount.id)
            .map((envelope) => ({
              virtualAccountId: virtualAccount.id,
              envelopeId: envelope.id,
              name: envelope.name,
              balanceMinor: envelope.balanceMinor,
            })),
        ],
      })),
    [virtualAccounts, envelopes, t],
  );

  const [categoriesByVirtualAccount, setCategoriesByVirtualAccount] = useState<
    Record<number, Category[]>
  >({});
  useEffect(() => {
    const missing = virtualAccounts
      .map((row) => row.id)
      .filter((id) => categoriesByVirtualAccount[id] === undefined);
    if (missing.length === 0) return;
    let cancelled = false;
    void Promise.all(
      missing.map(async (id) => {
        try {
          return [id, await categoriesApi.list(id)] as const;
        } catch {
          return [id, [] as Category[]] as const;
        }
      }),
    ).then((pairs) => {
      if (cancelled) return;
      setCategoriesByVirtualAccount((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });
    return () => {
      cancelled = true;
    };
  }, [virtualAccounts, categoriesByVirtualAccount]);

  const categoriesOf = (virtualAccountId: number): Category[] =>
    (categoriesByVirtualAccount[virtualAccountId] ?? []).filter((c) => c.archivedAt === null);
  const categoryName = (virtualAccountId: number | null, categoryId: number | null): string =>
    (categoryId !== null && virtualAccountId !== null
      ? (categoriesByVirtualAccount[virtualAccountId] ?? []).find((c) => c.id === categoryId)?.name
      : undefined) ?? t('envelope.uncategorized');

  return { ...owned, groups, categoriesOf, categoryName };
}

/** The rows of a destination; always one, even empty, so every destination has its input. */
export function rowsFor(rows: readonly AllocateRow[], destination: Destination): AllocateRow[] {
  return rows.filter(
    (row) =>
      row.virtualAccountId === destination.virtualAccountId &&
      row.envelopeId === destination.envelopeId,
  );
}

/** A destination's name and served balance on the left, `right` (its controls or parts) beside it. */
export function DestinationRow({
  name,
  balanceMinor,
  currency,
  right,
}: {
  name: string;
  balanceMinor: number | null;
  currency: string | null;
  right: ReactNode;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const labelInk = resolveTokens(useTheme()).textLabel('surfaceRaised');
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1.5, alignItems: { sm: 'center' } }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="div" sx={{ fontSize: 14, fontWeight: 500 }}>
          {name}
        </Typography>
        {balanceMinor !== null && (
          <Typography component="div" sx={{ fontSize: 12, color: labelInk }}>
            {t('allocate.balance', { amount: formatMoneyMinor(balanceMinor, currency, locale) })}
          </Typography>
        )}
      </Box>
      {right}
    </Stack>
  );
}

interface DestinationGroupListProps {
  groups: readonly DestinationGroup[];
  rows: AllocateRow[];
  /** Each row's previewed (or resolved) amount, by row index. */
  shown: readonly (number | null)[];
  /** The server's `resolved[]`, by row index, once it has spoken (MM-D-125). */
  resolved: readonly (number | null)[] | null;
  rowErrors: Record<string, string>;
  /** The operating currency every amount is in. */
  currency: string | null;
  categoriesOf: (virtualAccountId: number) => Category[];
  contraOf?: (row: AllocateRow) => Contra;
  /** A pre-filled row's "origin · actor". */
  captionOf?: (row: AllocateRow) => ReactNode;
  /** Whether a group takes input (default: every group). */
  editable?: (group: DestinationGroup) => boolean;
  /** Under a group's header (a locked group's note). */
  groupNotice?: (group: DestinationGroup) => ReactNode;
  /** A destination of a group that takes no input: its read-only parts. */
  readOnlyDestination?: (group: DestinationGroup, destination: Destination) => ReactNode;
  /** After a group's destinations (parts outside them). */
  groupFooter?: (group: DestinationGroup) => ReactNode;
  onChange: (key: string, patch: Partial<AllocateRow>) => void;
  onRemove: (key: string) => void;
  onAdd: (group: DestinationGroup) => void;
  onFocusAmount: (key: string) => void;
}

/** The destination groups with their rows (audit 06 § Overrides 4). */
export function DestinationGroupList({
  groups,
  rows,
  shown,
  resolved,
  rowErrors,
  currency,
  categoriesOf,
  contraOf = () => null,
  captionOf = () => null,
  editable = () => true,
  groupNotice,
  readOnlyDestination,
  groupFooter,
  onChange,
  onRemove,
  onAdd,
  onFocusAmount,
}: DestinationGroupListProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const labelInk = tokens.textLabel('surfaceRaised');
  const rowLabel = (destination: Destination, group: DestinationGroup) =>
    groups.length > 1 ? `${destination.name} · ${group.virtualAccount.name}` : destination.name;
  return groups.map((group) => {
    const editing = editable(group);
    return (
      <Box
        key={group.virtualAccount.id}
        data-destination-group={group.virtualAccount.id}
        sx={{ mb: 1.5 }}
      >
        <Stack
          direction="row"
          sx={{ alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, py: 0.5 }}
        >
          <Typography component="h4" sx={{ fontSize: 13, fontWeight: 500, m: 0 }}>
            {group.virtualAccount.name}
          </Typography>
          {group.virtualAccount.balanceMinor !== null && (
            <Typography component="span" sx={{ fontSize: 12, color: labelInk }}>
              {t('allocate.balance', {
                amount: formatMoneyMinor(
                  group.virtualAccount.balanceMinor,
                  group.virtualAccount.currency || currency,
                  locale,
                ),
              })}
            </Typography>
          )}
        </Stack>
        {groupNotice?.(group)}
        {group.destinations.map((destination) => {
          const own = rowsFor(rows, destination)[0];
          const label = rowLabel(destination, group);
          return (
            <Box
              key={`${destination.virtualAccountId}:${destination.envelopeId ?? 'u'}`}
              sx={{ borderBottom: `1px solid ${tokens.line}`, py: 1 }}
            >
              <DestinationRow
                name={destination.name}
                balanceMinor={destination.balanceMinor}
                currency={currency}
                right={
                  editing && own !== undefined ? (
                    <RowControls
                      row={own}
                      label={label}
                      categories={categoriesOf(group.virtualAccount.id)}
                      contra={contraOf(own)}
                      caption={captionOf(own)}
                      shownMinor={shown[rows.indexOf(own)] ?? null}
                      resolvedMinor={resolved?.[rows.indexOf(own)] ?? null}
                      error={rowErrors[own.key]}
                      currency={currency}
                      onChange={(patch) => onChange(own.key, patch)}
                      onFocusAmount={() => onFocusAmount(own.key)}
                    />
                  ) : (
                    readOnlyDestination?.(group, destination)
                  )
                }
              />
            </Box>
          );
        })}
        {groupFooter?.(group)}
        {editing && (
          <ExtraRows
            group={group}
            rows={rows}
            categories={categoriesOf(group.virtualAccount.id)}
            shown={shown}
            resolved={resolved}
            rowErrors={rowErrors}
            currency={currency}
            contraOf={contraOf}
            onChange={onChange}
            onRemove={onRemove}
            onFocusAmount={onFocusAmount}
          />
        )}
        {editing && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAdd(group)}
            sx={{ mt: 0.5 }}
          >
            {t('allocate.addRow')}
          </Button>
        )}
      </Box>
    );
  });
}

interface RowControlsProps {
  row: AllocateRow;
  /** The destination's accessible name ("Groceries · Household"). */
  label: string;
  categories: Category[];
  contra: Contra;
  /** The live part's "origin · actor", for a pre-filled row. */
  caption: ReactNode;
  shownMinor: number | null;
  resolvedMinor: number | null;
  error: string | undefined;
  currency: string | null;
  onChange: (patch: Partial<AllocateRow>) => void;
  onFocusAmount: () => void;
}

/** One row's category select, mode toggle and 120px input. */
function RowControls({
  row,
  label,
  categories,
  contra,
  caption,
  shownMinor,
  resolvedMinor,
  error,
  currency,
  onChange,
  onFocusAmount,
}: RowControlsProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const categoryLabelId = useId();
  return (
    <Stack sx={{ gap: 0.5, alignItems: { sm: 'flex-end' } }}>
      <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id={categoryLabelId}>
            {t('allocate.category', { destination: label })}
          </InputLabel>
          <Select
            labelId={categoryLabelId}
            label={t('allocate.category', { destination: label })}
            value={typeof row.categoryId === 'number' ? String(row.categoryId) : row.categoryId}
            onChange={(event) => {
              const value = event.target.value;
              onChange({
                categoryId: value === '' || value === UNCATEGORIZED ? value : Number(value),
              });
            }}
          >
            {/* Sends `uncategorized: true`: never the payee's default by accident (TMMAPP-0114 B5). */}
            <MenuItem value={UNCATEGORIZED}>{t('envelope.uncategorized')}</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={String(category.id)}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {contra !== null && (
          <Typography variant="micro" component="span" sx={{ color: tokens.text2 }}>
            {t(`allocate.contra.${contra}`)}
          </Typography>
        )}
        <ModeToggle label={label} value={row.mode} onChange={(mode) => onChange({ mode })} />
        {row.mode !== 'rest' ? (
          <TextField
            size="small"
            value={row.value}
            onChange={(event) => onChange({ value: event.target.value })}
            onFocus={onFocusAmount}
            error={error !== undefined}
            helperText={error}
            sx={{ width: 120 }}
            slotProps={{
              htmlInput: {
                inputMode: 'decimal',
                'aria-label': t(row.mode === 'percent' ? 'allocate.percent' : 'allocate.amount', {
                  destination: label,
                }),
                style: { textAlign: 'end', fontVariantNumeric: 'tabular-nums' },
              },
              input:
                row.mode === 'percent'
                  ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                  : {},
            }}
          />
        ) : (
          <Box sx={{ width: 120, textAlign: 'end' }}>
            <MoneyText minor={shownMinor} currency={currency} variant="body2" />
          </Box>
        )}
      </Stack>
      {caption !== null && (
        <Typography variant="caption" sx={{ color: tokens.text2 }}>
          {caption}
        </Typography>
      )}
      {resolvedMinor !== null && (
        <Typography variant="caption" sx={{ color: tokens.errorText }}>
          {t('allocate.resolved', { amount: formatMoneyMinor(resolvedMinor, currency, locale) })}
        </Typography>
      )}
      {/* A row error the amount field can't carry (the rest mode): the row's own line. */}
      {row.mode === 'rest' && error !== undefined && (
        <Typography variant="caption" role="alert" sx={{ color: tokens.errorText }}>
          {error}
        </Typography>
      )}
    </Stack>
  );
}

/** The Amount / % / Rest toggle: a radio group in the segmented style (visual contract §5). */
export function ModeToggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: AllocateMode;
  onChange: (mode: AllocateMode) => void;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const labels: Record<AllocateMode, string> = {
    amount: t('allocate.modeAmount'),
    percent: t('allocate.modePercent'),
    rest: t('allocate.modeRest'),
  };
  return (
    <Box
      role="radiogroup"
      aria-label={t('allocate.mode', { destination: label })}
      sx={{
        display: 'inline-flex',
        border: `1px solid ${tokens.line}`,
        borderRadius: `${tokens.radius.control}px`,
        overflow: 'hidden',
        bgcolor: tokens.surface,
      }}
      onKeyDown={(event) => {
        const at = MODES.indexOf(value);
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          onChange(MODES[(at + 1) % MODES.length]!);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          onChange(MODES[(at - 1 + MODES.length) % MODES.length]!);
        }
      }}
    >
      {MODES.map((mode) => {
        const selected = mode === value;
        return (
          <ButtonBase
            key={mode}
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(mode)}
            sx={{
              px: 1.25,
              py: 0.5,
              fontSize: 12,
              fontWeight: 500,
              color: selected ? tokens.link : tokens.text2,
              bgcolor: selected ? tokens.selected : 'transparent',
              '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: -2 },
            }}
          >
            {labels[mode]}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

/** Rows added with "+ Add a row": each picks its destination within the group. */
function ExtraRows({
  group,
  rows,
  categories,
  shown,
  resolved,
  rowErrors,
  currency,
  contraOf,
  onChange,
  onRemove,
  onFocusAmount,
}: {
  group: DestinationGroup;
  rows: AllocateRow[];
  categories: Category[];
  shown: readonly (number | null)[];
  resolved: readonly (number | null)[] | null;
  rowErrors: Record<string, string>;
  currency: string | null;
  contraOf: (row: AllocateRow) => Contra;
  onChange: (key: string, patch: Partial<AllocateRow>) => void;
  onRemove: (key: string) => void;
  onFocusAmount: (key: string) => void;
}) {
  const { t } = useTranslation();
  // The extra rows are the ones past each destination's first row.
  const firstKeys = new Set(
    group.destinations.map((destination) => rowsFor(rows, destination)[0]?.key),
  );
  const extras = rows.filter(
    (row) => row.virtualAccountId === group.virtualAccount.id && !firstKeys.has(row.key),
  );
  if (extras.length === 0) return null;
  return (
    <Stack sx={{ gap: 1, mt: 1 }}>
      {extras.map((row) => {
        const destination =
          group.destinations.find((d) => d.envelopeId === row.envelopeId) ?? group.destinations[0]!;
        const label = `${destination.name} · ${group.virtualAccount.name}`;
        const destinationLabelId = `${row.key}-destination`;
        return (
          <Stack
            key={row.key}
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ gap: 1.5, alignItems: { sm: 'center' } }}
          >
            <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
              <InputLabel id={destinationLabelId}>{t('allocate.rowDestination')}</InputLabel>
              <Select
                labelId={destinationLabelId}
                label={t('allocate.rowDestination')}
                value={row.envelopeId === null ? 'u' : String(row.envelopeId)}
                onChange={(event) =>
                  onChange(row.key, {
                    envelopeId: event.target.value === 'u' ? null : Number(event.target.value),
                  })
                }
              >
                {group.destinations.map((d) => (
                  <MenuItem
                    key={d.envelopeId ?? 'u'}
                    value={d.envelopeId === null ? 'u' : String(d.envelopeId)}
                  >
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <RowControls
              row={row}
              label={label}
              categories={categories}
              contra={contraOf(row)}
              caption={null}
              shownMinor={shown[rows.indexOf(row)] ?? null}
              resolvedMinor={resolved?.[rows.indexOf(row)] ?? null}
              error={rowErrors[row.key]}
              currency={currency}
              onChange={(patch) => onChange(row.key, patch)}
              onFocusAmount={() => onFocusAmount(row.key)}
            />
            <IconButton aria-label={t('allocate.removeRow')} onClick={() => onRemove(row.key)}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        );
      })}
    </Stack>
  );
}
